import { v4 } from "uuid";

import { RuleValueFormat, ValueSource } from "constants/ruleBooks";

import toast from "lib/toast";

import { ValueSourceExt } from "./constants";
import {
  getValueFormatByFieldType,
  parseCheckValue,
  parseInternalIndex,
} from "./lib";
import { getDataTypeByPath, getPathParts } from "./Schema/schema";

export function convertToExternalSchemaFieldDataSource(internalDataSource) {
  return {
    source: internalDataSource.source,
    value: internalDataSource.value,
    value_type: internalDataSource.valueType,
  };
}

export function convertToExternalAggregateDataSource(internalDataSource) {
  return {
    criteria_output_index: parseInternalIndex(
      internalDataSource.criteria_output_index,
    ),
    source: ValueSourceExt.SUM,
    value: internalDataSource.value,
    value_type: internalDataSource.valueType,
  };
}

function convertToExternalDataSource(dataSource) {
  if (
    [
      ValueSource.FIELD,
      ValueSource.RULESET_FIELD,
      ValueSource.DEPENDENCY_VALUE,
      ValueSource.CONSTANT,
      ValueSource.GLOBAL_BUSINESS,
    ].includes(dataSource.source)
  ) {
    return convertToExternalSchemaFieldDataSource(dataSource);
  }
  if ([ValueSource.SUM, ValueSource.COUNT].includes(dataSource.source)) {
    return convertToExternalAggregateDataSource(dataSource);
  }
  // eslint-disable-next-line no-console
  console.error(
    `Unknown data source type "${dataSource.source}", passing through`,
  );
  return dataSource;
}

export function convertToExternalCheck(internalCheck) {
  const externalCheck = {
    comparator: internalCheck.comparator,
    field: convertToExternalDataSource(internalCheck.field),
    values: internalCheck.values,
  };

  // if the field is a boolean, we need to parse the value
  externalCheck.values = internalCheck.values.map(value =>
    parseCheckValue(externalCheck.field.value_type, value),
  );

  return externalCheck;
}

export function convertToExternalChecks(internalCheckGroups) {
  return internalCheckGroups.map(internalChecks =>
    internalChecks.map(convertToExternalCheck),
  );
}

export function convertToExternalCriterion(internalCriterion) {
  const checks = convertToExternalChecks(internalCriterion.checkGroups);
  if (!internalCriterion.query?.value) {
    return { checks };
  }

  return {
    checks,
    query: {
      input_field: "DEPRECATED",
      path: internalCriterion.query.value,
      query_field: "DEPRECATED",
    },
  };
}

export function convertToExternalFee(internalFee, index) {
  const fee = {
    apportion_from_source: internalFee.apportionFromSource,
    enabled_query: convertToExternalDataSource(internalFee.enabledQuery),
    name: internalFee.name,
    operation: internalFee.operation,
    operation_input: convertToExternalDataSource(internalFee.operationInput),
    source_index: undefined,
  };
  if (
    typeof internalFee.sourceIndex === "number" &&
    internalFee.sourceIndex >= 0 &&
    internalFee.sourceIndex < index + 1
  ) {
    fee.source_index = internalFee.sourceIndex;
  }
  return fee;
}

export function convertToExternalFees(internalFees) {
  return internalFees.map(convertToExternalFee);
}

export function convertToExternalInputField(internalInputPath) {
  if (!internalInputPath) {
    return null;
  }
  // For backwards compatibility the path needs to be converted from dot notation to quoted bracket notation
  // e.g. $.foo.bar[*].baz[*] to $["foo"]["bar"][*]["baz"][*]
  return getPathParts(internalInputPath)
    .map((pathPart: string) => {
      if (pathPart === "$") {
        return pathPart;
      }
      // isolate the [*] part of the path, when it exists
      const allSubSelectorIndex = pathPart.indexOf("[*]");
      return allSubSelectorIndex
        ? `["${pathPart.substring(0, allSubSelectorIndex)}"][*]`
        : pathPart;
    })
    .join("");
}

export function convertToExternalRule(internalRule, schema, parentDataType) {
  const dataType = internalRule.input_field
    ? getDataTypeByPath(schema, internalRule.input_field.value)
    : parentDataType;

  const contentObject = {
    billing_tags: internalRule.billing_tags,
    category: internalRule.category,
    fees: convertToExternalFees(internalRule.fees),
    from_business: convertToExternalDataSource(internalRule.from_business),
    id_field: convertToExternalDataSource({
      value: dataType ? dataType.idAttribute : "id",
      source: ValueSource.FIELD,
    }),
    include_if_zero: internalRule.include_if_zero || false,
    price_multiplier: internalRule.price_multiplier,
    tax_type: internalRule.tax_type,
    to_business: convertToExternalDataSource(internalRule.to_business),
    value: {
      amount: convertToExternalDataSource(internalRule.value.amount),
      offset: convertToExternalDataSource(internalRule.value.offset),
      rounding_strategy: internalRule.value.rounding_strategy,
      rounding_value: internalRule.value.rounding_value,
      output_as_single_unit: internalRule.value.output_as_single_unit,
      units: convertToExternalDataSource(internalRule.value.units),
      unit_limit: convertToExternalDataSource(internalRule.value.unit_limit),
      unit_offset: convertToExternalDataSource(internalRule.value.unit_offset),
    },
  };

  const quantityOutputFormat =
    internalRule.quantity_output_format || RuleValueFormat.UNITS;
  const unitAmountOutputFormat =
    internalRule.unit_amount_output_format || RuleValueFormat.UNITS;

  const quantityRawFormat = getValueFormatByFieldType(
    contentObject.value.units.value_type,
  );
  const unitAmountRawFormat = getValueFormatByFieldType(
    contentObject.value.amount.value_type,
    RuleValueFormat.CENTS,
  );

  const isRelativeInputField =
    internalRule.prerequisite_rule_ids?.length > 0 || internalRule.rule_set_id;

  const inputField = convertToExternalInputField(
    isRelativeInputField
      ? internalRule.input_field?.value || null
      : `$.${internalRule.input_field.value}`,
  );

  try {
    return {
      comment: internalRule.comment,
      content: JSON.stringify(contentObject),
      criteria: internalRule.criteria.map(convertToExternalCriterion),
      gl_code: internalRule.gl_code,
      id: internalRule.id || v4(),
      input_field: inputField,
      invoice_line_item_template: internalRule.invoice_line_item_template,
      master_rule_id: internalRule.master_rule_id,
      name: internalRule.name,
      order: internalRule.order,
      prerequisite_rule_ids: internalRule.prerequisite_rule_ids || [],
      quantity_output_format: quantityOutputFormat,
      quantity_raw_format: quantityRawFormat,
      rule_book_id: internalRule.rule_book_id,
      rule_set_id: internalRule.rule_set_id,
      title_template: internalRule.title_template,
      unit_amount_output_format: unitAmountOutputFormat,
      unit_amount_raw_format: unitAmountRawFormat,
    };
  } catch (e) {
    toast.error(`Error saving rule ${internalRule.name}`);
    return null;
  }
}
